import { markRaw } from 'vue'
import StaffingAgencyClientNameColumn from './StaffingAgencyClientNameColumn.vue'
import StaffingAgencyClientRelationColumn from './StaffingAgencyClientRelationColumn.vue'
import StaffingAgencyClientPlacementsColumn from './StaffingAgencyClientPlacementsColumn.vue'
import StaffingAgencyClientLocationColumn from './StaffingAgencyClientLocationColumn.vue'
import StaffingAgencyClientAddedColumn from './StaffingAgencyClientAddedColumn.vue'
import {
  StaffingAgencyClientAddedColumnFragment,
  StaffingAgencyClientAddedColumnFragmentDoc,
  StaffingAgencyClientLocationColumnFragment,
  StaffingAgencyClientLocationColumnFragmentDoc,
  StaffingAgencyClientNameColumnFragment,
  StaffingAgencyClientNameColumnFragmentDoc,
  StaffingAgencyClientPlacementsColumnFragment,
  StaffingAgencyClientPlacementsColumnFragmentDoc,
  StaffingAgencyClientRelationColumnFragment,
  StaffingAgencyClientRelationColumnFragmentDoc,
  StaffingAgencyClientOrderByColumn,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { sortPair } from '@/components/GraphqlDataTable/columns/sortPair'

const columns: ColumnMaps = {
  StaffingAgencyClientNameColumn: {
    fragment: StaffingAgencyClientNameColumnFragmentDoc,
    component: markRaw(StaffingAgencyClientNameColumn),
    columnDef: {
      title: $t('Client'),
      key: 'StaffingAgencyClientNameColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(StaffingAgencyClientNameColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Client name',
        extraction: (row: StaffingAgencyClientNameColumnFragment) => row.client.name,
      },
    ],
    sort: sortPair(StaffingAgencyClientOrderByColumn.ClientName, $t('Client'), $t('A - Z'), $t('Z - A')),
  },
  StaffingAgencyClientRelationColumn: {
    fragment: StaffingAgencyClientRelationColumnFragmentDoc,
    component: markRaw(StaffingAgencyClientRelationColumn),
    columnDef: {
      title: $t('Relation'),
      key: 'StaffingAgencyClientRelationColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(StaffingAgencyClientRelationColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Relation',
        extraction: (row: StaffingAgencyClientRelationColumnFragment) =>
          row.isDirect ? $t('Direct') : row.managingCompany.name,
      },
    ],
    sort: sortPair(StaffingAgencyClientOrderByColumn.Relation, $t('Relation'), $t('Direct first'), $t('Direct last')),
  },
  StaffingAgencyClientPlacementsColumn: {
    fragment: StaffingAgencyClientPlacementsColumnFragmentDoc,
    component: markRaw(StaffingAgencyClientPlacementsColumn),
    columnDef: {
      title: $t('Placements | Active'),
      key: 'StaffingAgencyClientPlacementsColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(StaffingAgencyClientPlacementsColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Placements',
        extraction: (row: StaffingAgencyClientPlacementsColumnFragment) => `${row.hiresCount}`,
      },
      {
        title: 'Active placements',
        extraction: (row: StaffingAgencyClientPlacementsColumnFragment) => `${row.activeHiresCount}`,
      },
    ],
    sort: sortPair(StaffingAgencyClientOrderByColumn.Placements, $t('Placements'), $t('Low to high'), $t('High to low')),
  },
  StaffingAgencyClientLocationColumn: {
    fragment: StaffingAgencyClientLocationColumnFragmentDoc,
    component: markRaw(StaffingAgencyClientLocationColumn),
    columnDef: {
      title: $t('Location'),
      key: 'StaffingAgencyClientLocationColumn',
      renderUsing: {
        component: markRaw(StaffingAgencyClientLocationColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Country',
        extraction: (row: StaffingAgencyClientLocationColumnFragment) => row.clientCountry?.name ?? '',
      },
      {
        title: 'Region',
        extraction: (row: StaffingAgencyClientLocationColumnFragment) => row.client.region?.name ?? '',
      },
    ],
  },
  StaffingAgencyClientAddedColumn: {
    fragment: StaffingAgencyClientAddedColumnFragmentDoc,
    component: markRaw(StaffingAgencyClientAddedColumn),
    columnDef: {
      title: $t('Added'),
      key: 'StaffingAgencyClientAddedColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(StaffingAgencyClientAddedColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Added',
        extraction: (row: StaffingAgencyClientAddedColumnFragment) => row.createdAt,
      },
    ],
    sort: sortPair(StaffingAgencyClientOrderByColumn.ClientSince, $t('Added'), $t('Old to new'), $t('New to old')),
  },
}

export default columns
